import { withWidth } from "@material-ui/core";
import Types from "prop-types";
import React from "react";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import eventAnalytics from "../../analytikaV2";
import { ARGUS_APM } from "../../apm";
import Galileo from "../../galileo";
import { loadSheild } from "../../sheild";
import { ANALYTIKA_EVENTS } from "../../utils/constants";
import {
  getUserinfo,
  submitInvitationToMarilyn,
  submitInvitationToMoT,
  getInvitationSummary,
} from "../gateway/InvitationGateway";
import "./MotInvitation.css";
import SolutionIcon from "../../images/solutionIcon.svg";
import UserAddIcon from "../../images/userAdd.svg";

class MotInvitation extends React.Component {
  constructor(props) {
    super(props);
    loadSheild();

    this.state = {
      error: null,
      name: "",
      phone_number: null,
      email: null,
      user_urn: null,
      token: this.props.match.params.token,
      submitting: true,
      userId: 0,
      isV2Enabled: false,
      isAccessScopeCountEnabled: false,
      companiesCount: 0,
      brandsCount: 0,
      outletsCount: 0,
      roleName: "",
    };

    this.onCancel = this.onCancel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  async componentDidMount() {
    await this.initializePage();
  }

  async initializePage() {
    eventAnalytics.setPageName(ANALYTIKA_EVENTS.PAGE.BUSINESS_INVITATION);
    eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_VIEW_PAGE, {
      page_name: ANALYTIKA_EVENTS.PAGE.BUSINESS_INVITATION,
    });

    try {
      const data = this.props.userinfo || (await getUserinfo()).data;
      const isV2Enabled = await Galileo.fetchIsMotInvitationV2Enabled();
      const isAccessScopeCountEnabled = await Galileo.fetchIsMotAccessScopeCountEnabled();

      let inviteSummary = {};
      if (isV2Enabled) {
        try {
          const { data: summaryData } = await getInvitationSummary(this.state.token);
          inviteSummary = summaryData;
        } catch (summaryError) {
          ARGUS_APM.captureError(summaryError);
        }
      }

      this.setState({
        name: data.given_name,
        phone_number: data.phone_number,
        email: data.email,
        user_urn: `urn:${(data.kind || "customer").toLowerCase()}:${data.sub}`,
        userId: data.sub,
        kind: data.kind,
        isV2Enabled,
        isAccessScopeCountEnabled,
        companiesCount: inviteSummary.companiesCount || 0,
        brandsCount: inviteSummary.brandsCount || 0,
        outletsCount: inviteSummary.outletsCount || 0,
        roleName: inviteSummary.roleDisplayName || inviteSummary.roleName || "",
        submitting: false,
      });
    } catch (error) {
      ARGUS_APM.captureError(error);
      this.setState({
        error: { message: this.props.t("business_invitation:ERROR_UNKNOWN") },
        submitting: false,
      });
    }
  }

  async onCancel(event) {
    eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_TAP_BUTTON, {
      button_name: "not_now",
      invitation_code: this.state.token,
      user_id: this.state.userId,
    });
    event.preventDefault();
    await eventAnalytics.flush();
    window.location.assign("/logout");
  }

  async onSubmit(event) {
    eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_TAP_BUTTON, {
      button_name: "accept_invitation",
      invitation_code: this.state.token,
      user_id: this.state.userId,
    });
    event.preventDefault();

    this.setState({
      submitting: true,
      error: null,
    });

    try {
      eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBREQUEST, {
        request_type: "invitation_code_marilyn_submit",
        user_id: this.state.userId,
      });
      await submitInvitationToMarilyn({
        code: this.state.token,
        phone: this.state.phone_number,
        ...(this.state.kind === "MERCHANT" && { email: this.state.email }),
      });
      eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBRESPOSE, {
        request_type: "invitation_code_marilyn_submit",
        request_status: "success",
        user_id: this.state.userId,
      });

      await this.tryToProcessInvitationAtMot();

      await eventAnalytics.flush();

      const redirectUrl = `${process.env.REACT_APP_PARTNERS_PORTAL_HOST}`;

      window.location.replace(redirectUrl);
    } catch (e) {
      if (e.message === "MotGwError") {
        this.handleMotAuthErrors(e.cause);
      } else {
        this.handleMarilynErrors(e);
      }
    } finally {
      this.setState({ submitting: false });
    }
  }

  handleMarilynErrors(e) {
    const { t } = this.props;
    const { token } = this.state;
    eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBRESPOSE, {
      request_type: "invitation_code_marilyn_submit",
      request_status: "failed",
      error_code_string: e.response?.data?.code,
      error_description: e.response?.data?.message,
      user_id: this.state.userId,
    });
    this.onRequestError(e);
    const codeToTranslationKey = {
      "BU-0010": "business_invitation:invitation_code_expired",
      "BU-0011": "business_invitation:invitation_code_already_used",
      "BU-0012": "business_invitation:invitation_code_inactivated",
      "BU-0013": "business_invitation:invitation_code_revoked",
      "BU-0014": "business_invitation:invitation_code_cancelled",
      "BU-0015": "business_invitation:invitation_code_rejected",
      "BU-0016": "business_invitation:invitation_code_bad_shape",
    };

    switch (e.response?.status) {
      case 401:
        this.setState({
          error: { message: t("business_invitation:unauthorized") },
        });
        break;
      case 404:
        this.setState({
          error: {
            message: t("business_invitation:invitation_code_not_found", {
              token,
            }),
          },
        });
        break;
      case 400:
        switch (e.response.data.code) {
          case "BU-0010":
          case "BU-0011":
          case "BU-0012":
          case "BU-0013":
          case "BU-0014":
          case "BU-0015":
          case "BU-0016": {
            const translationKey = codeToTranslationKey[e.response.data.code];
            this.setState({
              error: {
                message: t(translationKey, { token }),
              },
            });
            break;
          }
          default:
            this.setState({
              error: {
                message: t("business_invitation:invitation_code_invalid", {
                  token,
                }),
              },
            });
            break;
        }
        break;
      default:
        this.setState({
          error: { message: t("business_invitation:ERROR_UNKNOWN") },
        });
    }
  }

  async tryToProcessInvitationAtMot() {
    const { userId } = this.state;
    if (await Galileo.fetchIsMotInvitationS2S(userId)) {
      return;
    }
    try {
      eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBREQUEST, {
        request_type: "invitation_code_mot_auth_submit",
        user_id: this.state.userId,
      });
      await submitInvitationToMoT({
        code: this.state.token,
      });
      eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBRESPOSE, {
        request_type: "invitation_code_mot_auth_submit",
        request_status: "success",
        user_id: this.state.userId,
      });
    } catch (e) {
      throw new Error("MotGwError", { cause: e });
    }
  }

  handleMotAuthErrors(e) {
    const { t } = this.props;

    eventAnalytics.publishV2(ANALYTIKA_EVENTS.IDNW_SEND_WEBRESPOSE, {
      request_type: "invitation_code_mot_auth_submit",
      request_status: "failed",
      error_code_string: e.response?.data?.code,
      error_description: e.response?.data?.message,
      user_id: this.state.userId,
    });
    this.onRequestError(e);

    switch (e.response?.status) {
      default:
        this.setState({
          error: { message: t("business_invitation:mot_request_failed") },
        });
    }
  }

  onRequestError(e) {
    ARGUS_APM.captureError(e);
  }

  renderV2() {
    const { t, submitError } = this.props;
    const {
      error,
      submitting,
      name,
      phone_number,
      email,
      companiesCount,
      brandsCount,
      outletsCount,
      roleName,
      isAccessScopeCountEnabled,
    } = this.state;

    return (
      <div className="mot-v2-wrapper">
        <div className="mot-invitation-header">
          <div className="mot-invitation-header-title">Careem Business</div>
          <div className="mot-invitation-header-subtitle">
            {t("business_invitation:invitation_request")}
          </div>
        </div>
        <form id="formInvite" onSubmit={this.onSubmit} autoComplete="off">
          <div className="mot-main-content">
            <div className="mot-invitation-title">
              <h2>{t("business_invitation:account_assignment_ready")}</h2>
              <p>{t("business_invitation:review_details_to_proceed")}</p>
            </div>

            {!submitError && error && (
              <div
                className="invalid show mot-error-message"
                role="alert"
                aria-live="assertive"
              >
                {error.message}
              </div>
            )}
            {submitError && (
              <div
                className="invalid show mot-error-message"
                role="alert"
                aria-live="assertive"
              >
                {submitError}
              </div>
            )}

            <div className="mot-user-card">
              <img src={UserAddIcon} className="mot-user-icon" alt="" aria-hidden="true" />
              <div className="mot-user-info">
                <div className="mot-user-label">{t("business_invitation:assigning_to")}</div>
                <div className="mot-user-name">{name}</div>
                {phone_number && (
                  <div className="mot-user-phone">
                    <span className="mot-phone-icon" aria-hidden="true">📱</span>
                    {phone_number}
                  </div>
                )}
                {email && <div className="mot-user-email">{email}</div>}
              </div>
            </div>

            {roleName && (
              <div className="mot-role-card mot-role-card-green">
                <div className="mot-role-icon-circle" aria-hidden="true">
                  <img src={SolutionIcon} className="mot-role-icon" alt="" aria-hidden="true" />
                </div>
                <div className="mot-role-info">
                  <div className="mot-role-label">{t("business_invitation:assigned_role")}</div>
                  <div className="mot-role-title">{roleName}</div>
                </div>
              </div>
            )}

            {isAccessScopeCountEnabled && (
              <div className="mot-access-scope">
                <div className="mot-access-scope-label">{t("business_invitation:access_scope")}</div>
                <div className="mot-access-scope-grid">
                  <div className="mot-access-scope-box">
                    <div className="mot-access-scope-count">{companiesCount || "-"}</div>
                    <div className="mot-access-scope-name">{t("business_invitation:companies")}</div>
                  </div>
                  <div className="mot-access-scope-box">
                    <div className="mot-access-scope-count">{brandsCount || "-"}</div>
                    <div className="mot-access-scope-name">{t("business_invitation:brands")}</div>
                  </div>
                  <div className="mot-access-scope-box">
                    <div className="mot-access-scope-count">{outletsCount || "-"}</div>
                    <div className="mot-access-scope-name">{t("business_invitation:outlets")}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="mot-buttons-container">
              <button
                className="mot-btn-primary"
                type="submit"
                disabled={submitting}
                aria-label={
                  submitting
                    ? t("common:please_wait")
                    : t("business_invitation:accept_invitation")
                }
              >
                {submitting
                  ? t("common:please_wait")
                  : t("business_invitation:accept_invitation")}
              </button>
              <button
                className="mot-btn-text"
                type="button"
                onClick={this.onCancel}
                disabled={submitting}
                aria-label={t("business_invitation:not_now")}
              >
                {t("business_invitation:not_now")}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  render() {
    const { t, submitError } = this.props;
    const { error, submitting, name, phone_number, email, isV2Enabled } = this.state;

    if (isV2Enabled) {
      return this.renderV2();
    }

    const contact_info = phone_number || email;

    return (
      <form id="formInvite" onSubmit={this.onSubmit} autoComplete="off">
        <div dir="ltr">
          <h1 className="heading-h1-express center express-common">
            {t("business_invitation:business_invitation_header")}
          </h1>
        </div>
        <h3 className="heading-h3-express center express-common">
          {t("business_invitation:greetings", { name, contact_info })}
        </h3>
        {!submitError && error && (
          <div className="invalid show" role="alert" aria-live="assertive">
            {error.message}
          </div>
        )}
        {submitError && (
          <div className="invalid show" role="alert" aria-live="assertive">
            {submitError}
          </div>
        )}
        <input
          className="btn-careem btn-careem-primary"
          type="submit"
          value={
            submitting
              ? t("common:please_wait")
              : t("business_invitation:accept_invitation")
          }
          disabled={submitting}
          aria-label={
            submitting
              ? t("common:please_wait")
              : t("business_invitation:accept_invitation")
          }
        />
        <input
          className="no-btn-careem"
          type="button"
          onClick={this.onCancel}
          value={t("business_invitation:not_now")}
          disabled={submitting}
          aria-label={t("business_invitation:not_now")}
        />
      </form>
    );
  }
}

MotInvitation.propTypes = {
  clientId: Types.string,
  match: Types.object.isRequired,
  t: Types.func,
  submitError: Types.any,
  width: Types.string,
  userinfo: Types.object,
};

export default withWidth()(withRouter(withTranslation()(MotInvitation)));
export const MotInvitationUnwrapped = MotInvitation;

// Re-exported for backward compatibility with existing test imports
export { getUserinfo, submitInvitationToMarilyn, submitInvitationToMoT } from "../gateway/InvitationGateway";
